import { useFormik } from "formik";
import React, { forwardRef, useImperativeHandle } from "react";
import { useFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { persologElementQuestionClassForm_PersologQuestionClassFragment$key } from "@relay/persologElementQuestionClassForm_PersologQuestionClassFragment.graphql";

import { createPersologElementQuestionClassFormSchema } from "./persolog-element-question-class-form.consts";
import { PERSOLOG_QUESTION_CLASS_FRAGMENT } from "./persolog-element-question-class-form.graphql";
import {
	PersologElementQuestionClassFormProps,
	PersologElementQuestionClassFormRef,
	PersologElementQuestionClassFormState,
} from "./persolog-element-question-class-form.types";
import { DefaultTextAreaComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";

export const PersologElementQuestionClassForm = forwardRef<
	PersologElementQuestionClassFormRef,
	PersologElementQuestionClassFormProps
>(function PersologElementQuestionClassForm(
	{ persologElementQuestionClassFragmentRef, onSubmit, classType },
	ref,
) {
	const questionClass =
		useFragment<persologElementQuestionClassForm_PersologQuestionClassFragment$key>(
			PERSOLOG_QUESTION_CLASS_FRAGMENT,
			persologElementQuestionClassFragmentRef ?? null,
		);

	const { t } = useTranslation(["common", "validation"]);

	const initialValues: PersologElementQuestionClassFormState = {
		text: questionClass?.text ?? "",
		classType: classType,
	};

	const formik = useFormik<PersologElementQuestionClassFormState>({
		initialValues,
		validationSchema: createPersologElementQuestionClassFormSchema(t),
		onSubmit: (values) => onSubmit?.(values),
	});

	useImperativeHandle(ref, () => ({
		classType: classType,
		values: formik.values,
		submit: formik.submitForm,
		validate: () => formik.validateForm().then((errors) => Object.keys(errors).length === 0),
	}));

	return (
		<ValidatedField<PersologElementQuestionClassFormState, string>
			name={"text"}
			label={t("forms.answer")}
			formikConfig={formik}
			required
			component={DefaultTextAreaComponent}
		/>
	);
});
